import { Stack, Tooltip, Typography, useTheme } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  EmptyStateWrapper,
  InnerItemContainer,
} from './AnalyticsDetails.styled'
import * as sxStyles from './AnalyticsDetails.styled'
import { useQuery } from 'react-query'
import { Dispatch, SetStateAction } from 'react'
import dayjs from 'dayjs'
import { AnalyticsListMode } from '../list/Analytics'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { CheckMarkCircleSmall } from '../../../../components'
import CancelIcon from '@mui/icons-material/Cancel'
import { useAnalyticsServices } from '../services/services'
import { handleDeliveryIcon } from 'pages/Shipment/components/consts'
dayjs.extend(customParseFormat)

interface IDeliveryListDetails {
  listKey: number
  date: string
  productId: string
  selectedDeliveryId?: string
  setSelectedDeliveryId?: Dispatch<SetStateAction<string | undefined>>
  mode: AnalyticsListMode
}

export const consistencyIcons = {
  Consistent: {
    icon: <CheckMarkCircleSmall />,
    color: 'success',
    activeColor: 'success',
  },
  Inconsistent: {
    icon: <CancelIcon fontSize="medium" />,
    color: 'error',
    activeColor: 'white',
  },
}

export const DeliveryListDetails: React.FC<IDeliveryListDetails> = ({
  listKey,
  date,
  productId,
  selectedDeliveryId,
  setSelectedDeliveryId,
  mode,
}) => {
  const isBakerModule = mode === 'products'
  const { getDeliveriesByArrivalDate } = useAnalyticsServices(isBakerModule)
  const theme = useTheme()

  const { data, isLoading } = useQuery(
    ['deliveries-by-date', productId, date],
    () =>
      getDeliveriesByArrivalDate({
        flour_product_id: isBakerModule ? undefined : productId,
        baker_product_id: isBakerModule ? productId : undefined,
        arrival_date: date,
        filters: {
          post_aging: mode === 'post-aging',
        },
      }),
    { enabled: !!productId && !!date }
  )

  const deliveries = data?.results?.[0]?.deliveries ?? []

  return (
    <InnerItemContainer key={listKey}>
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : deliveries.length === 0 ? (
        <EmptyStateWrapper>
          <InfoOutlinedIcon fontSize="small" />
          <Typography sx={sxStyles.noDedliveriesTypography}>
            No Deliveries
          </Typography>
        </EmptyStateWrapper>
      ) : (
        deliveries.map((delivery) => {
          const consistencyIcon =
            consistencyIcons[
              delivery.consistency_label as keyof typeof consistencyIcons
            ] ?? consistencyIcons['Inconsistent']

          const Icon = handleDeliveryIcon(delivery)

          return (
            <Stack
              sx={{
                ...sxStyles.listInnerBox,
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor:
                  delivery.id === selectedDeliveryId
                    ? theme.palette.primary.main
                    : 'unset',
                color: delivery.id === selectedDeliveryId ? 'white' : 'unset',
              }}
              key={delivery.id}
              onClick={() => setSelectedDeliveryId(delivery.id)}
            >
              <Typography>{date}</Typography>
              <Typography>
                {dayjs(delivery.creation_time, 'hh:mm:ss A').format('hh:mm A')}
              </Typography>
              <Tooltip title={delivery.name}>
                <Typography noWrap sx={{ maxWidth: 130 }}>
                  {delivery.name}
                </Typography>
              </Tooltip>
              <Tooltip title={delivery.consistency_label}>
                <Typography
                  color={
                    delivery.id === selectedDeliveryId
                      ? consistencyIcon.activeColor
                      : consistencyIcon.color
                  }
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                  }}
                >
                  {delivery?.discarded ? Icon : consistencyIcon.icon}
                </Typography>
              </Tooltip>
            </Stack>
          )
        })
      )}
    </InnerItemContainer>
  )
}
